import { useMemo, useState } from "react";
import { useListQuery } from "./lib/query";
import { ListPanel } from "./components/ui";
import { useSort, SortTh } from "./lib/sortable";
import { api } from "./lib/api";
import { Rate, Legend, Vintage, type Freshness } from "./components/provenance";

/** Per-county vault coverage, led by the number a canvasser actually depends on.
 *
 * WHAT THIS SCREEN USED TO SAY, AND WHY IT WAS THE WRONG THING. It headlined "Statewide cover
 * 73.4%" — the share of addresses carrying a parcel bounding box. That is the ENHANCEMENT layer.
 * The number that answers "can the crew find the door" is rooftop-quality pins over all
 * addresses: 11,003,256 of 12,310,602, 89.4%, established in 0207 and named in the System Guide as
 * the figure the portal should lead with. It appeared on no screen. A staffer reading 73.4% as the
 * headline concludes a quarter of Texas is uncanvassable, and sends people at a problem that is not
 * there — while 1,136,791 conflicted pins, the largest genuinely actionable pile in the vault, were
 * displayed nowhere at all.
 *
 * AND WHY THE VERDICT COLUMN WAS UNREADABLE. Eleven counties shared one sentence — "Thin cover.
 * Usually means the county publishes street names without house numbers" — which was the wrong
 * explanation for seven of them, and the column carrying it was clipped off the right edge of the
 * table, so nobody read the sentence that was wrong. 0211 gives each county its own sentence with
 * its own numbers; this file gives that sentence a full-width row of its own instead of a column
 * that has to compete with nine numeric ones.
 *
 * THE VERDICT IS STILL THE POINT. Sorted by percentage, a county whose appraisal district publishes
 * no address at all looks identical to one we failed to parse. `parser_gap` — the only bucket that
 * is a queue of OUR work — sorts first and is the only one coloured as a problem.
 */
type Row = {
  fips: number; county: string | null;
  address_points: number; parcel_extents: number | null;
  pct_covered: number | null;
  parcels_total: number | null; situs_present: number | null; situs_numbered: number | null; keyed: number | null;
  on_the_building: number | null; on_the_lot: number | null;
  // null = this county has not been rolled up since the column existed. Never read as zero.
  lot_with_building: number | null; conflicted: number | null;
  pct_rooftop: number | null; pct_on_the_lot: number | null;
  // MEASURED, not labelled. null = this county has never been footprint-checked.
  checked: number | null; on_a_building: number | null; pct_on_a_building: number | null;
  not_on_a_building: number | null; esd_policy: string;
  pct_own: number | null; credibility: string;
  vintage: string | null; loaded_at: string | null;
  verdict: string; note: string | null;
  // WHAT THE APPRAISAL DISTRICT SAYS THE LOT IS (0222). null everywhere means this county
  // publishes no land use — 77 of the 246 counties with parcels do not. Null is not zero here
  // any more than anywhere else: "we cannot say what these lots are" is a different sentence
  // from "none of them are houses", and only one of them is true.
  sf_addresses: number | null; sf_has_building: number | null; sf_no_building: number | null;
  sf_building_unknown: number | null; sf_on_the_building: number | null;
  sf_checked: number | null; sf_on_a_building: number | null;
  mf_addresses: number | null; vacant_addresses: number | null; land_use_unknown: number | null;
  // MEASURED and LABELLED, side by side and over the same denominator. Returned together on
  // purpose: the only reason anyone notices these drifting apart is that both are on screen.
  pct_sf_on_a_building: number | null; pct_sf_labelled_rooftop: number | null;
  pct_land_use_known: number | null;
};
type Totals = {
  counties: number; address_points: number; parcel_extents: number;
  on_the_building: number; checked: number; on_a_building: number; not_on_a_building: number;
  on_the_lot: number; lot_counties: number;
  lot_with_building: number | null; conflicted: number;
  conflicted_credible: number; conflicted_noisy: number; conflicted_unmeasured: number;
  ours: number; county_data: number; thin: number; unloaded: number; addresses_not_ok: number;
  sf_addresses: number; sf_on_the_building: number; sf_has_building: number;
  sf_counties: number; land_use_unknown: number;
  sf_checked: number; sf_on_a_building: number;
};
type Cov = { counties: Row[]; totals: Totals; freshness?: Freshness | null };

/** How much a conflict in this county is worth believing.
 *
 *  'suspect' means ONE thing: the address point did not land on the parcel whose address matches
 *  it — and `conflicted` counts the suspects NOTHING HAS CORRECTED YET, because the tier ladder
 *  reads an active placement ahead of the county's geocode_quality. The count therefore falls as
 *  the placement passes run, which is the opposite of how every version of this screen's prose
 *  described it. What a conflict is worth depends entirely on the county, and 0165 measured it — a suspect where
 *  92% of addresses sit on their own parcel is a real outlier; one where the county scores 35% is
 *  mostly telling you the parcel file is poor. The scorecard shipped the 1,136,791 total with the
 *  words "a human decides" and no band, which points people at a pile a fifth of which is noise.
 */
const CRED: Record<string, { label: string; tone: string; hint: string }> = {
  high:       { label: "high",   tone: "#15803d", hint: "80%+ of this county's addresses sit on their own parcel — a conflict here is a real outlier." },
  good:       { label: "good",   tone: "#15803d", hint: "60–80% own-parcel. A conflict here is usually worth looking at." },
  mixed:      { label: "mixed",  tone: "#b45309", hint: "40–60% own-parcel. Some of these conflicts are the county's parcel file, not the address." },
  low:        { label: "low",    tone: "#6b7280", hint: "Under 40% own-parcel. A conflict here mostly means the parcel file is poor; reviewing one at a time is wasted effort." },
  unmeasured: { label: "—",      tone: "#6b7280", hint: "No parcel check has run for this county, so there is nothing to judge a conflict against." },
};

/** `kind` is what the label MEANS, and it is the only thing the header buckets and the row colour
 *  read. Adding a verdict without giving it a kind should be impossible to do quietly, which is why
 *  the map carries it rather than the call sites keeping their own Sets — the old ACTIONABLE set
 *  put `thin` in the same bucket as `parser_gap` and told staff eleven counties were theirs to fix
 *  when the true count was zero. */
type Kind = "ours" | "county" | "unloaded" | "watch" | "ok";
const VERDICT: Record<string, { label: string; kind: Kind }> = {
  parser_gap:      { label: "Ours to fix",            kind: "ours" },
  sparse_situs:    { label: "File is mostly blank",   kind: "county" },
  road_names_only: { label: "Roads, not doors",       kind: "county" },
  shared_situs:    { label: "Many parcels, one door", kind: "county" },
  source_gap:      { label: "No address published",   kind: "county" },
  no_parcel_file:  { label: "No parcel file",         kind: "county" },
  thin:            { label: "File is short",          kind: "watch" },
  never_loaded:    { label: "Never loaded",           kind: "unloaded" },
  not_measured:    { label: "Not measured",           kind: "unloaded" },
  not_rolled_up:   { label: "Not rolled up",          kind: "unloaded" },
  ok:              { label: "OK",                     kind: "ok" },
};
const KIND: Record<Kind, { tone: string; blurb: string }> = {
  ours:     { tone: "#b91c1c", blurb: "The county published an address we failed to read. This is a bug, and it is ours." },
  county:   { tone: "#6b7280", blurb: "Limited by what the appraisal district publishes. Nothing here is ours to fix." },
  watch:    { tone: "#b45309", blurb: "Read in full, but the county's parcel file is smaller than the county. Worth re-checking on the next vintage." },
  unloaded: { tone: "#6b7280", blurb: "No measurement yet — run the load or the rollup before reading anything into the number." },
  ok:       { tone: "#15803d", blurb: "" },
};
const kindOf = (v: string): Kind => VERDICT[v]?.kind ?? "unloaded";

/** A county needs a look for either of TWO independent reasons, and until 0213 the list only knew
 *  one of them.
 *
 *  The old list was `verdict !== "ok"` — the county's parcel FILE is short. Every one of those
 *  nineteen counties is between 81% and 100% on a rooftop; not one is a county where a canvasser
 *  has a problem. Meanwhile Nueces holds 113,058 addresses with 6.5% of them on a rooftop and 92%
 *  pinned to a lot centroid, grades `ok`, and appeared nowhere — while Loving County's 503
 *  addresses at 94.4% rooftop sat at the top of "needs attention".
 *
 *  A lot pin is not a gradient: exactly ten counties have any, and they hold all 170,555. So the
 *  test is `> 0` rather than a threshold somebody has to defend.
 */
const needsALook = (r: Row) =>
  r.verdict !== "ok" || (r.on_the_lot ?? 0) > 0 || r.esd_policy === "C" || r.esd_policy === "D";

/** What this county's 911 authority actually does, measured rather than believed.
 *
 *  TxGIO aggregates 48 different 911 authorities, each with its own placement policy - rooftop,
 *  parcel centroid or road centreline - and publishes NO placement-method field. So every
 *  rooftop-family label, `rooftop_snapped` included, means only "TxGIO gave us this". Medina has
 *  16,425 pins labelled `rooftop_snapped`, all sourced `txgio`, averaging 52.6 m from the building
 *  coordinate on their own parcel. `footprint_check` is the only witness.
 *
 *  A county in group D will never improve on a refresh - its authority does not place on buildings
 *  as policy. That is the opposite response from a group A county that is merely behind.
 */
const POLICY: Record<string, { label: string; tone: string; hint: string }> = {
  A:         { label: "A - rooftop",     tone: "#15803d", hint: "70%+ of this county's pins are on a building. Its 911 authority places on structures; a refresh keeps it current." },
  B:         { label: "B - mixed",       tone: "#65a30d", hint: "45-70%. The authority places on buildings inconsistently - the misses hide inside a respectable-looking score, and this band holds the largest pile in Texas." },
  C:         { label: "C - mostly not",  tone: "#b45309", hint: "20-45%. Mostly not placed on buildings. A refresh will not fix this; only a placement pass of ours will." },
  D:         { label: "D - not rooftop", tone: "#b91c1c", hint: "Under 20%. This authority does not place on buildings as policy. No refresh will ever improve it." },
  unrated:   { label: "-",               tone: "#6b7280", hint: "Fewer than 2,000 addresses checked - too few to grade the county's placement policy." },
  unchecked: { label: "not checked",     tone: "#6b7280", hint: "No footprint check has run for this county." },
};
const labelOf = (v: string) => VERDICT[v]?.label ?? v;

const n = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString());
const pct = (v: number | null | undefined) => (v == null ? "—" : `${v}%`);
const share = (part: number, whole: number) => (whole > 0 ? `${((100 * part) / whole).toFixed(1)}%` : "—");

type Filter = "attention" | "ours" | "all";

export function VaultCoverage() {
  const [filter, setFilter] = useState<Filter>("attention");
  const { query, reload } = useListQuery<Cov>(
    "vault-coverage",
    (signal) => api<Cov>("/geocode/vault-coverage", { signal }),
  );

  return (
    <div>
      <div className="card" style={{ padding: 12, marginBottom: 10 }}>
        <div style={{ fontWeight: 700 }}>Vault coverage by county</div>
        <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
          What a canvasser gets when they open a walk list — and, where a county falls short, whose
          shortfall it is.
        </div>
      </div>

      <ListPanel
        query={query}
        isEmpty={(d) => d.counties.length === 0}
        empty={<div className="card muted" style={{ padding: 14 }}>No counties have been loaded into the vault yet.</div>}
        onRetry={reload}
      >
        {(d) => <Screen data={d} filter={filter} setFilter={setFilter} />}
      </ListPanel>
    </div>
  );
}

function Screen({ data, filter, setFilter }: {
  data: Cov; filter: Filter; setFilter: (f: Filter) => void;
}) {
  const { counties, totals } = data;
  const problems = counties.filter(needsALook).length;
  const healthy = totals.counties - problems;

  return (
    <>
      <Headline totals={totals} problems={problems} />
      {/* Every figure in the header and the table below comes out of one snapshot of county_daily.
          The date belongs under them, once, rather than beside each number. */}
      <Vintage f={data.freshness} />
      <div className="card" style={{ padding: 0, marginTop: 10 }}>
        <div className="row" style={{ gap: 6, padding: "10px 14px", flexWrap: "wrap", alignItems: "center",
                                      borderBottom: "1px solid var(--border,#e5e7eb)" }}>
          <Chip on={filter === "attention"} onClick={() => setFilter("attention")}
                label={`Needs a look (${counties.filter(needsALook).length})`} />
          <Chip on={filter === "ours"} onClick={() => setFilter("ours")}
                label={`Ours to fix (${totals.ours})`} tone={totals.ours > 0 ? "#b91c1c" : undefined} />
          <Chip on={filter === "all"} onClick={() => setFilter("all")} label={`All ${totals.counties} counties`} />
          <span className="muted" style={{ fontSize: 12, marginLeft: "auto" }}>
            {healthy} counties are healthy.
          </span>
        </div>
        <Table counties={counties} totals={totals} filter={filter} />
      </div>
    </>
  );
}

/** The header, in the order the questions actually get asked.
 *
 *  Rooftop first because it is the operational number. Conflicted second because it is the only
 *  large pile anyone can act on. Parcel cover third, LABELLED as the enhancement layer, because
 *  presented bare at the top it reads as "27% of Texas is missing" and it is not that. */
function Headline({ totals, problems }: { totals: Totals; problems: number }) {
  const addr = totals.address_points;
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="row" style={{ gap: 26, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div>
          <div className="muted" style={{ fontSize: 11 }}>Residential pins measured onto a building</div>
          {/* MEASURED, AND OVER `sf_checked` (0227). This tile used to divide the rooftop LABEL by
              sf_addresses -- two mistakes at once. household_confidence_tier reads geocode_quality
              for any address we did not place, and TxGIO inherits that from 48 911 authorities and
              publishes no placement-method field; Bexar reads 99.8 per cent labelled against 90.8
              measured. And dividing by sf_addresses rather than sf_checked reports a county the
              footprint pass has not reached as a failure -- Collin at 61.4 per cent for a scan that
              has not run there.

              THE COUNTY COUNT IS PART OF THE NUMBER. This rate speaks for the counties that publish
              land use and no others; a county with no residential denominator is left OUT rather
              than scored zero, and saying so is the difference between a measurement and a claim. */}
          <Rate part={totals.sf_on_a_building} whole={totals.sf_checked} kind="measured"
                of={`single-family pins tested against Overture footprints, in ${totals.sf_counties} of ${totals.counties} counties`}
                unmeasured={totals.sf_addresses > 0
                  ? "No single-family pin has been footprint-checked yet, so there is no measured rate. This is not a zero."
                  : "No county has been rolled up with land use yet — run the land-use pass and the county rollup. This is not a zero."} />
          <div className="muted" style={{ fontSize: 12, marginTop: 6, maxWidth: "44ch" }}>
            {totals.sf_checked > 0 && (
              <span title="household_confidence_tier reads geocode_quality for any address we did not place ourselves, and TxGIO inherits that from 48 911 authorities and publishes no placement-method field. A rooftop label is not evidence that the pin is on a building.">
                {share(totals.sf_on_the_building, totals.sf_checked)} of the same pins merely carry a
                rooftop label — read the two together, never one alone.
              </span>
            )}{" "}
            {totals.sf_checked > 0 && totals.sf_addresses > totals.sf_checked && <>{n(totals.sf_addresses - totals.sf_checked)} single-family
            addresses have not been checked and are in neither figure.</>}{" "}
            {totals.land_use_unknown > 0 && <>{n(totals.land_use_unknown)} addresses carry no land
            use yet and are in neither.</>}
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11 }}>Pins on a building, all land uses</div>
          {/* OVER `checked`, NOT OVER EVERY ADDRESS. An address the footprint pass has not reached
              is not a miss, and dividing by the whole vault turns an unrun scan into a failure --
              the mistake 0197, 0207 and 0210 each had to remove from a different screen. */}
          <Rate part={totals.on_a_building} whole={totals.checked} kind="measured"
                of="pins tested against Overture building footprints"
                unmeasured="No pin has been footprint-checked yet. This is not a zero." />
          {/* THE LABEL IS NOT THE MEASUREMENT. This headlined 89.4% -- a count of geocode_quality
              labels -- until 0215. TxGIO aggregates 48 911 authorities with different placement
              policies and publishes no placement-method field, so every rooftop-family label
              including `rooftop_snapped` is inherited. footprint_check is the witness.
              The old sub-line here read "{on_a_building} of {address_points}", quietly swapping
              the denominator the headline above it uses. */}
          <div className="muted" style={{ fontSize: 12, marginTop: 6, maxWidth: "42ch" }}>
            {addr > totals.checked && <>{n(addr - totals.checked)} addresses have never been
            tested and are in neither number. </>}
            <span title="household_confidence_tier folds county_rooftop, rooftop, rooftop_verified and rooftop_snapped into one bucket. TxGIO publishes no placement-method field, so none of them is evidence that the pin is on a building.">
              {n(totals.on_the_building)} carry a rooftop label instead of a measurement.
            </span>
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11 }}>Pins in conflict, worth a look</div>
          <div style={{ fontWeight: 800, fontSize: 30, lineHeight: 1.1, color: totals.conflicted_credible > 0 ? "#b45309" : undefined }}>
            {n(totals.conflicted_credible)}
          </div>
          {/* THE TOTAL, AND WHY IT IS NOT THE QUEUE. A conflict is the address point failing to land
              on the parcel whose address matches it AND nothing having corrected it since. In a
              county where most addresses DO land on their own parcel that is a real outlier; where
              they do not, it is mostly the parcel file. Showing it as one number sends people at a
              pile a fifth of which is noise, which is what the triage queue has ordered around
              since 0165.

              AND IT IS A QUEUE, NOT A DIAGNOSIS, which every version of this text got wrong. The
              tier ladder (0216, and the same ladder in rollup_county_daily) reads the PLACEMENT
              first: an address we have placed takes its tier from how we placed it and never from
              the county's geocode_quality. So placing a pin removes it from this count. Tarrant
              holds 55,577 suspect addresses and reports 52,114 conflicted -- the 3,463 difference
              is not a discrepancy, it is the work already done. Text describing a permanent
              property of the data, over a number that is a backlog, is the same defect as a label
              headlined as a measurement. */}
          <div className="muted" style={{ fontSize: 12 }}>
            of {n(totals.conflicted)} total. The other {n(totals.conflicted_noisy + totals.conflicted_unmeasured)} are
            in counties whose parcel file is too poor for the flag to mean much.
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11 }}>Pinned to a lot, not a building</div>
          <div style={{ fontWeight: 800, fontSize: 30, lineHeight: 1.1, color: totals.on_the_lot > 0 ? "#b45309" : undefined }}>
            {n(totals.on_the_lot)}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            in {totals.lot_counties} counties — the pin is the parcel centroid, which on a large lot
            can be a quarter-mile from the house.{" "}
            {totals.lot_with_building == null
              ? <>Re-run the county rollup to see how many already have a building under them.</>
              : <><b>{n(totals.lot_with_building)}</b> already have a scanned building on their parcel
                  and need no new data.</>}
          </div>
        </div>
        <div style={{ borderLeft: "1px solid var(--border,#e5e7eb)", paddingLeft: 22 }}>
          <div className="muted" style={{ fontSize: 11 }}>Parcel cover (enhancement layer)</div>
          <div style={{ fontWeight: 700, fontSize: 22, lineHeight: 1.2 }}>
            {share(totals.parcel_extents, addr)}
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {n(totals.parcel_extents)} parcel outlines. Lot shape sharpens a pin; it is not needed to
            knock a door.
          </div>
        </div>
        <div>
          <div className="muted" style={{ fontSize: 11 }}>Addresses in a county that falls short</div>
          <div style={{ fontWeight: 700, fontSize: 22, lineHeight: 1.2 }}>{n(totals.addresses_not_ok)}</div>
          <div className="muted" style={{ fontSize: 12 }}>
            {share(totals.addresses_not_ok, addr)} of the state, across {problems} counties
          </div>
        </div>
      </div>
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--border,#27303f)" }}>
        <Legend />
      </div>
    </div>
  );
}

function Table({ counties, totals, filter }: { counties: Row[]; totals: Totals; filter: Filter }) {
  const rows = useMemo(() => {
    if (filter === "all") return counties;
    if (filter === "ours") return counties.filter((r) => kindOf(r.verdict) === "ours");
    return counties.filter(needsALook);
  }, [counties, filter]);

  // MISSES, NOT PERCENTAGE. The 81 mixed counties hold 2,343,053 pins that are not on a building --
  // more than the broken counties and the good ones combined -- and every one of them scores
  // 45-70%, which reads as fine. Harris alone is 442,227. Sorting by share buries them.
  const sort = useSort<Row>(rows, (r, k) => r[k as keyof Row], "not_on_a_building", "desc");

  if (rows.length === 0) {
    // NOT an error, and worth saying plainly: the filter found nothing because nothing is wrong.
    return (
      <div className="muted" style={{ padding: 14 }}>
        {filter === "ours"
          ? <>Nothing here is ours. Every county we can read, we have read in full — the only gaps
              left are in what the appraisal districts publish. Switch to <b>Needs a look</b> to see
              those.</>
          : <>Every loaded county has good cover. Switch to <b>All {totals.counties} counties</b> for
              the full list.</>}
      </div>
    );
  }

  return (
    <div style={{ overflow: "auto", maxHeight: 620 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ position: "sticky", top: 0, background: "var(--bg,#fff)", zIndex: 1 }}>
            <SortTh label="County" col="county" sort={sort} />
            <SortTh label="Addresses" col="address_points" sort={sort} align="right" />
            <SortTh label="Single family" col="sf_addresses" sort={sort} align="right"
                    title="Addresses on a lot the appraisal district files as state category A. Blank where the county publishes no land use — that is unanswerable, not zero." />
            <SortTh label="SF on a building" col="pct_sf_on_a_building" sort={sort} align="right"
                    title="MEASURED: of this county's single-family pins that have been tested against Overture footprints, the share found inside one. The operational number, and the one the portal headlines. Blank where nothing has been checked — that is unmeasured, not zero." />
            <SortTh label="On a building" col="pct_on_a_building" sort={sort} align="right"
                    title="MEASURED against Overture footprints, not read off a label. Null where the county has never been checked." />
            <SortTh label="Misses" col="not_on_a_building" sort={sort} align="right"
                    title="Pins tested and found not to be on a building. The size of the problem in this county — not a work queue, because how many can be safely moved has not been measured." />
            <SortTh label="ESD policy" col="esd_policy" sort={sort}
                    title="What this county's 911 authority actually does, measured. A refresh helps A and B; it will never help C or D." />
            <SortTh label="Rooftop label" col="pct_rooftop" sort={sort} align="right"
                    title="Share carrying a rooftop-family geocode label. Inherited from TxGIO, which publishes no placement-method field — kept for comparison, not evidence." />
            <SortTh label="On a lot" col="pct_on_the_lot" sort={sort} align="right"
                    title="Addresses pinned to the parcel centroid rather than a building. Someone registered on the property, not at a door." />
            <SortTh label="Parcel cover" col="pct_covered" sort={sort} align="right"
                    title="Addresses with a parcel outline. An enhancement, not a prerequisite." />
            <SortTh label="In the file" col="parcels_total" sort={sort} align="right"
                    title="Parcel records the county publishes." />
            <SortTh label="…with a street" col="situs_present" sort={sort} align="right"
                    title="Records carrying a street name. PO boxes and unnumbered roads count here." />
            {/* The number the verdict is judged on since 0200: a road is not a door. */}
            <SortTh label="…with a number" col="situs_numbered" sort={sort} align="right"
                    title="Records carrying a house number — the only ones that could be a door." />
            <SortTh label="Keyed" col="keyed" sort={sort} align="right"
                    title="Records that produced an address key." />
            <SortTh label="Conflicts" col="conflicted" sort={sort} align="right"
                    title="Addresses whose pin did not land on the parcel whose address matches it and which nothing has corrected yet — placing a pin removes one from this count. Also how much this county's own data lets you believe the flag." />
            <SortTh label="Verdict" col="verdict" sort={sort} />
          </tr>
        </thead>
        <tbody>
          {sort.sorted.map((r) => <CountyRows key={r.fips} r={r} />)}
        </tbody>
      </table>
    </div>
  );
}

function CountyRows({ r }: { r: Row }) {
  const kind = kindOf(r.verdict);
  const tone = KIND[kind].tone;
  const cred = CRED[r.credibility] ?? CRED.unmeasured;
  const pol = POLICY[r.esd_policy] ?? POLICY.unchecked;
  // WHAT THIS COUNTY NEEDS, which is the whole point of measuring the policy rather than the label.
  // A refresh moves A and B. It will never move C or D, whose authorities do not place on buildings
  // as policy -- and where the county publishes no usable parcel either, our placement pass has
  // nothing to stand on and the honest answer is that this one is not ours to fix yet.
  const policyNote = (r.esd_policy === "C" || r.esd_policy === "D") && r.not_on_a_building
    ? `${n(r.not_on_a_building)} of this county's ${n(r.checked)} checked pins are not on a building `
      + `(${r.pct_on_a_building}% are). Its 911 authority does not place on structures as policy, so `
      + `no TxGIO refresh will improve this. `
      + ((r.parcel_extents ?? 0) < 0.25 * r.address_points
          ? `And the county publishes too little usable parcel data for our placement pass to stand `
            + `on — placement cannot help here yet.`
          : `How many can be safely moved is NOT measured: proximity is not permission, and only a `
            + `pin whose parcel claims one address with one building on it can move without risking `
            + `the neighbour's house.`)
    : null;
  // Composed here rather than in the function, because it is not an explanation of the VERDICT —
  // a county can be perfectly graded and still have most of its pins on land. 0207's three axes.
  const lot = (r.on_the_lot ?? 0) > 0
    ? `${n(r.on_the_lot)} of this county's addresses (${r.pct_on_the_lot}%) are pinned to the parcel `
      + `centroid rather than a building — someone registered on the property, not at a door. On a `
      + `large lot that is a quarter-mile walk and a Not Home that was never a miss. `
      // An upper bound, said as one. A building on the parcel is not the placement pass agreeing to
      // use it: 0205's lot-size gate, two buildings of similar size and the parcel_n = 1 rule all
      // refuse cases this count includes.
      + (r.lot_with_building == null
          ? `How many of them already sit on a scanned building has not been measured since the `
            + `column was added — re-run the county rollup.`
          : `${n(r.lot_with_building)} of them already have a scanned building on their parcel, so `
            + `the building placement pass could move them with no new data — where its lot-size `
            + `and ambiguity rules allow.`)
    : null;
  const explain = r.verdict !== "ok" ? (r.note || KIND[kind].blurb) : null;
  // keyed CAN exceed the house-number count, and a reader seeing 25 against 9 with no explanation
  // reasonably concludes the data is corrupt. It is not: norm() and has_house_number() disagree on
  // a handful of situs formats, which is documented and harmless. Say so where it shows.
  const keyedOver = r.keyed != null && r.situs_numbered != null && r.keyed > r.situs_numbered;

  return (
    <>
      <tr style={{ background: kind === "ours" ? "rgba(185,28,28,.06)" : undefined,
                   borderTop: "1px solid var(--border,#f1f5f9)" }}>
        <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
          {r.county || "—"} <span className="muted" style={{ fontSize: 11 }}>{r.fips}</span>
        </td>
        <Num v={n(r.address_points)} />
        {/* AN EM DASH, NOT A ZERO. A county publishing no land use has no residential numbers at
            all; rendering 0 / 0.0% would sort it to the bottom of the screen as the worst county
            in Texas when nothing about it has been measured (0210's rule, in a new column). */}
        <td style={{ padding: "6px 10px", textAlign: "right" }}>
          {r.sf_addresses == null ? <span className="muted">—</span> : n(r.sf_addresses)}
        </td>
        <td style={{ padding: "6px 10px", textAlign: "right",
                     color: r.pct_sf_on_a_building != null && r.pct_sf_on_a_building < 90 ? "#b91c1c" : undefined,
                     fontWeight: r.pct_sf_on_a_building != null && r.pct_sf_on_a_building < 90 ? 700 : undefined }}
            title={r.pct_sf_labelled_rooftop != null
                   ? `${r.pct_sf_labelled_rooftop}% carry a rooftop label — inherited, not evidence`
                   : undefined}>
          {r.pct_sf_on_a_building == null ? <span className="muted">—</span> : pct(r.pct_sf_on_a_building)}
        </td>
        <Num v={pct(r.pct_on_a_building)} />
        <Num v={n(r.not_on_a_building)} />
        <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
          <span style={{ color: pol.tone, cursor: "help", fontSize: 12 }} title={pol.hint}>{pol.label}</span>
        </td>
        <td className="muted" style={{ padding: "6px 10px", textAlign: "right" }}>{pct(r.pct_rooftop)}</td>
        <td style={{ padding: "6px 10px", textAlign: "right", color: (r.on_the_lot ?? 0) > 0 ? "#b45309" : undefined }}>
          {(r.on_the_lot ?? 0) > 0 ? pct(r.pct_on_the_lot) : <span className="muted">—</span>}
        </td>
        <Num v={pct(r.pct_covered)} muted />
        <Num v={n(r.parcels_total)} muted />
        <Num v={n(r.situs_present)} muted />
        <Num v={n(r.situs_numbered)} />
        <td style={{ padding: "6px 10px", textAlign: "right" }}>
          {n(r.keyed)}
          {keyedOver && (
            <span className="muted" style={{ fontSize: 11, marginLeft: 3, cursor: "help" }}
                  title="Keyed exceeds the house-number count because the key parser and the house-number test disagree on a few situs formats. Known, harmless, and not extra data.">
              *
            </span>
          )}
        </td>
        <td style={{ padding: "6px 10px", textAlign: "right", whiteSpace: "nowrap" }}>
          {n(r.conflicted)}
          {r.conflicted != null && r.conflicted > 0 && (
            <span style={{ fontSize: 11, marginLeft: 5, color: cred.tone, cursor: "help" }}
                  title={`${cred.hint}${r.pct_own != null ? ` (${r.pct_own}% own-parcel)` : ""}`}>
              {cred.label}
            </span>
          )}
        </td>
        <td style={{ padding: "6px 10px", whiteSpace: "nowrap" }}>
          <span style={{ color: tone, fontWeight: kind === "ours" ? 700 : 500 }}>{labelOf(r.verdict)}</span>
        </td>
      </tr>
      {(explain || lot || policyNote) && (
        // The explanation gets the full width of the table. As a tenth column it was clipped off
        // the right edge, which is how eleven counties shared one wrong sentence unnoticed.
        <tr>
          <td colSpan={14} className="muted"
              style={{ padding: "0 10px 8px 10px", fontSize: 12, lineHeight: 1.45, maxWidth: 0 }}>
            {explain && (
              <span style={{ borderLeft: `3px solid ${tone}`, paddingLeft: 8, display: "block", marginBottom: lot ? 4 : 0 }}>
                {explain}
              </span>
            )}
            {lot && (
              <span style={{ borderLeft: "3px solid #b45309", paddingLeft: 8, display: "block",
                             marginBottom: policyNote ? 4 : 0 }}>
                {lot}
              </span>
            )}
            {policyNote && (
              <span style={{ borderLeft: `3px solid ${pol.tone}`, paddingLeft: 8, display: "block" }}>
                {policyNote}
              </span>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function Num({ v, muted }: { v: string; muted?: boolean }) {
  return <td className={muted ? "muted" : undefined} style={{ padding: "6px 10px", textAlign: "right" }}>{v}</td>;
}

function Chip({ label, on, onClick, tone }: { label: string; on: boolean; onClick: () => void; tone?: string }) {
  return (
    <button className={"btn sm" + (on ? " accent" : "")} onClick={onClick} style={{ color: on ? undefined : tone }}>
      {label}
    </button>
  );
}
